import type { AithorisConsentChangedPayload } from "../../types/aithoris-consent";
import { disableAnalytics, enableAnalytics } from "./analyticsLoader";

// Purpose ID as configured in the Aithoris dashboard. Unset = analytics never loads.
const ANALYTICS_PURPOSE_ID = process.env.REACT_APP_AITHORIS_ANALYTICS_PURPOSE_ID?.trim() || "";

let analyticsGranted = false;
let started = false;

const applyAnalyticsConsent = (purposes: Record<string, boolean> | undefined) => {
  const granted = !!ANALYTICS_PURPOSE_ID && purposes?.[ANALYTICS_PURPOSE_ID] === true;
  if (granted === analyticsGranted) return;
  analyticsGranted = granted;
  if (granted) enableAnalytics();
  else disableAnalytics();
};

const subscribe = () => {
  const sdk = window.AithorisConsent;
  if (!sdk || started) return !!sdk;
  started = true;

  // SDK emits consentChanged with reason "restore" for returning visitors, "save" on a new
  // choice and "withdraw" on withdrawal.
  sdk.on((event, data) => {
    if (event === "consentChanged") {
      applyAnalyticsConsent((data as AithorisConsentChangedPayload | undefined)?.purposes);
    } else if (event === "withdrawn") {
      applyAnalyticsConsent(undefined);
    }
  });

  // Covers the case where the SDK restored consent before this listener was attached.
  const instance = sdk.getInstance();
  instance
    ?.getConsent()
    .then((record) => {
      if (record && record.isActive !== false) applyAnalyticsConsent(record.purposes);
    })
    .catch(() => undefined);
  return true;
};

export const startConsentGatedAnalytics = () => {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV !== "production" && !ANALYTICS_PURPOSE_ID) {
    console.warn("REACT_APP_AITHORIS_ANALYTICS_PURPOSE_ID is not set; analytics will stay off.");
  }
  try {
    if (subscribe()) return;
    // SDK script is deferred and runs before the app bundle; retry once in case it was late.
    window.addEventListener(
      "load",
      () => {
        try {
          subscribe();
        } catch (error) {
          console.error("Aithoris consent SDK unavailable:", error);
        }
      },
      { once: true }
    );
  } catch (error) {
    console.error("Aithoris consent SDK unavailable:", error);
  }
};

export const openDataRightsRequest = () => {
  window.AithorisConsent?.getInstance()?.openDataRightsRequest();
};
